import type { FileWithLatestFileVersion } from "schemas/generated/file";

import * as React from "react";

import { nextJSProjectRouter } from "@editor/trpc/nextJSProject";

import { skipToken } from "@tanstack/react-query";

interface UseInfinitePages {
  projectId: string | null;
  limit?: number;
  search?: string;
  enabled?: boolean;
  orderBy?: {
    field: "name" | "updatedAt" | "createdAt";
    direction: "asc" | "desc";
  };
}

interface UseInfinitePagesReturn {
  pages: FileWithLatestFileVersion[];
  isLoading: boolean;
  error: unknown;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

/**
 * Custom hook for fetching and managing page lists with infinite pagination.
 * Provides cursor-based pagination for pages with search functionality.
 *
 * @param options - Configuration options for the pages query
 * @returns Page list data and pagination controls
 */
export function useInfinitePages({
  projectId,
  limit = 20,
  search,
  enabled = true,
  orderBy,
}: UseInfinitePages): UseInfinitePagesReturn {
  const {
    data: pagesData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = nextJSProjectRouter.listPages.useInfiniteQuery(
    projectId && enabled
      ? {
          projectId,
          limit,
          search,
          orderBy,
        }
      : skipToken,
    {
      getNextPageParam: ({ nextCursor }) => nextCursor,
    },
  );

  const pages = React.useMemo(
    () => pagesData?.pages?.flatMap((page) => page.pages) ?? [],
    [pagesData],
  );

  return {
    pages,
    isLoading,
    error,
    fetchNextPage: () => void fetchNextPage(),
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
  };
}
